import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  paymentSessionExpired,
  paymentStatus,
} from "../../../../services/billing";
import { useRazorpay } from "../../../../context/RazorpayContext";
import Hourglass3D from "./Hourglass";
import { useBilling } from "../../../../context/BillingContext";

function RazorpayModal({ navigationUrl }) {
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const {
    razorpayModalData,
    handleFailed,
    setRazorpayModalData,
    isRazorpayModalTimerLoader,
    setIsRazorpayModalTimerLoader,
    setPaymentStatus,
  } = useRazorpay();
  const { isThemeModal } = useBilling();
  const location = useLocation();
  const TOTAL_TIME = 5 * 60;
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [modalData, setModalData] = useState({
    open: false,
    type: "",
    message: "",
  });
  const modalDataRef = useRef(modalData);
  useEffect(() => {
    modalDataRef.current = modalData;
  }, [modalData]);
  const [cancelledByUser, setCancelledByUser] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const statusIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const currentUrl = location.pathname;

  const failedNavigationUrl = !razorpayModalData?.isPaymentExpired
    ? "/marketplace-accounts"
    : currentUrl === "/billing-section"
      ? "/marketplace-accounts"
      : "/billing-section";

  useEffect(() => {
    if (modalData.open) {
      document.body.classList.add("modal-open-blur");
    } else {
      document.body.classList.remove("modal-open-blur");
    }

    return () => {
      document.body.classList.remove("modal-open-blur");
    };
  }, [modalData.open]);
  useEffect(() => {
    if (!localStorage.getItem("razorpayModalData")) {
      localStorage.setItem(
        "razorpayModalData",
        JSON.stringify(razorpayModalData),
      );
    }
    setIsRazorpayModalTimerLoader(true);
    let startTime = localStorage.getItem("paymentStartTime");
    if (!startTime) {
      startTime = Date.now();
      localStorage.setItem("paymentStartTime", startTime);
    }

    const elapsed = Math.floor((Date.now() - Number(startTime)) / 1000);
    const remaining = TOTAL_TIME - elapsed;
    setTimeLeft(remaining > 0 ? remaining : 0);

    // Store interval IDs so we can clear them later
    let statusInterval, timerInterval;

    // Payment status check every 5 seconds
    statusIntervalRef.current = setInterval(async () => {
      //  STOP if cancelled
      if (cancelledByUser) return;
      try {
        const res = await paymentStatus();
        //  STOP again after API
        if (cancelledByUser) return;
        if (res?.data?.data?.invoice_status === "paid") {
          clearInterval(statusIntervalRef.current);
          clearInterval(timerIntervalRef.current);
          setModalData({
            open: true,
            type: "success",
            message: "Your transaction is complete! Click below to continue.",
          });
          setIsRazorpayModalTimerLoader(false);
          setPaymentStatus("success");
          localStorage.removeItem("razorpayModalData");
          localStorage.removeItem("paymentStartTime");
        } else if (res?.data?.data?.invoice_status === "failed") {
          clearInterval(statusIntervalRef.current);
          clearInterval(timerIntervalRef.current);
          setModalData({
            open: true,
            type: "failed",
            message: "Payment Failed. Please try again.",
          });
          setIsRazorpayModalTimerLoader(false);
          setPaymentStatus("failed");
          localStorage.removeItem("razorpayModalData");
          localStorage.removeItem("paymentStartTime");
        }
      } catch (err) {
        console.error("Payment status check failed:", err);
      }
    }, 5000);

    // Timer countdown
    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (cancelledByUser) return prev; //  ADD THIS
        if (
          modalDataRef.current?.open &&
          modalDataRef.current?.type === "confirm-cancel"
        )
          return prev; //  Pause during confirm-cancel

        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          handlePaymentExpired(null, null, "time expired", false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Warn user before closing/refreshing
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        "Do not refresh or close this page, otherwise payment may fail!";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(statusIntervalRef.current);
      clearInterval(timerIntervalRef.current);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [razorpayModalData?.invoiceId, navigationUrl]);

  // Handle payment expiry
  const handlePaymentExpired = async (
    statusInterval,
    timerInterval,
    reason = "time expired",
    isCancel = false,
  ) => {
    try {
      if (razorpayModalData?.isPaymentExpired) {
        await paymentSessionExpired();
        await new Promise((resolve) => setTimeout(resolve, 300));
        if (razorpayModalData?.isUpgradeCancel) {
          window.dispatchEvent(new Event("billingUpdated"));
        }
      }
    } catch (err) {
      console.error("Payment session expired API failed:", err);
    }

    setPaymentStatus(isCancel ? "expired" : "expired"); // Keeping 'expired' so the 'Try Again' button shows
    setModalData({
      open: true,
      type: "expired", // We use "expired" type because it renders the "Try Again" button properly
      message: isCancel
        ? "Payment cancelled by user. Please try again."
        : `Payment session ${reason}. Please try again.`,
    });
    localStorage.removeItem("razorpayModalData");
    localStorage.removeItem("paymentStartTime");
    clearInterval(statusIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    setIsRazorpayModalTimerLoader(false);
    setCancelLoading(false);
    setCancelledByUser(isCancel);
    navigate("/billing-section", { replace: true });
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handlePaymentSuccessNavigation = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.removeItem("razorpayModalData");
    localStorage.removeItem("paymentStartTime");

    setRazorpayModalData((prev) => ({
      ...prev,
      shortUrl: null,
      invoiceId: null,
      isLoading: false,
      isRazorPayModal: false,
      isBlockedRedirection: false,
    }));
    setIsRazorpayModalTimerLoader(false);
    await setModalData({ open: false, type: "", message: "" });
    if (razorpayModalData?.isUpgradeCancel) {
      window.dispatchEvent(new Event("billingUpdated"));
    }

    if (currentPath === navigationUrl) {
      if (typeof razorpayModalData?.refetchDataFn === "function") {
        razorpayModalData.refetchDataFn();
        setRazorpayModalData((prev) => ({
          ...prev,
          refetchDataFn: null,
        }));
      } else {
        await delay(200);
        window.dispatchEvent(new Event("refresh-page"));
      }
    } else {
      await navigate(navigationUrl, { replace: true });
    }
  };

  const handleCancelPayment = () => {
    setIsRazorpayModalTimerLoader(false); //  stop overlay
    setModalData({
      open: true,
      type: "confirm-cancel",
      message: "Are you sure you want to cancel?",
    });
  };

  return (
    <>
      {isRazorpayModalTimerLoader ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            color: "white",
            textAlign: "center",
          }}
        >
          {/* Circular Loader */}
          <div
            className="spinner-border text-warning"
            style={{ width: "4rem", height: "4rem", marginBottom: "20px" }}
            role="status"
          ></div>

          <p
            style={{
              color: "orange",
              fontWeight: "bold",
              marginBottom: "15px",
            }}
          >
            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>{" "}
            Do not refresh or close this page during payment!
          </p>

          <h3 style={{ fontWeight: "bold", marginBottom: "10px" }}>
            Processing Payment...
          </h3>
          <p style={{ fontSize: "1.2rem" }}>
            Please wait. Your payment session will expire in:
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
              background: "rgba(255,255,255,0.1)",
              padding: "10px 20px",
              borderRadius: "10px",
              marginTop: "10px",
            }}
          >
            <Hourglass3D /> {formatTime(timeLeft)}
          </div>

          {/*  Blocked Redirection Message */}
          {razorpayModalData?.isBlockedRedirection && (
            <div
              style={{
                marginTop: "25px",
                background: "rgba(255,255,255,0.1)",
                padding: "15px 20px",
                borderRadius: "10px",
                maxWidth: "600px",
              }}
            >
              <p style={{ color: "#ffcc00", marginBottom: "10px" }}>
                <i className="bi bi-ban stylish-ban"></i> Redirection Blocked or
                Refresh Detected!
              </p>
              <p style={{ marginBottom: "10px" }}>
                Please click the link below to complete your payment manually:
              </p>
              <a
                href={razorpayModalData?.shortUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  background: "rgb(78 140 161)",
                  color: "#fff",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  textDecoration: "none",
                  fontWeight: "bold",
                }}
              >
                <i
                  className="bi bi-lock-fill me-2"
                  style={{ color: "#ffcc00" }}
                ></i>{" "}
                Continue Payment
              </a>
              <p style={{ marginTop: "10px", color: "#aaa" }}>
                (We’ve also sent this link to your registered email and phone.)
              </p>
            </div>
          )}
          {/* Cancel Button (ADD HERE) */}
          <button
            onClick={handleCancelPayment}
            className="payment-loader-cancel-btn"
          >
            Cancel Payment
          </button>
        </div>
      ) : modalData.open ? (
        <div className="cancel-modal-overlay">
          <div
            className={`cancel-modal-container ${!isThemeModal ? "modal-force-dark" : ""}`}
          >
            {/* Glow */}
            <div className="cancel-modal-glow"></div>

            {/* Icon */}
            <div className="cancel-modal-icon-wrapper">
              {modalData.type === "success" ? (
                <div className="cancel-modal-icon-ring success">
                  <i className="bi bi-check2 cancel-modal-status-icon success"></i>
                </div>
              ) : modalData.type === "failed" ? (
                <div className="cancel-modal-icon-ring failed">
                  <i className="bi bi-x-lg cancel-modal-status-icon failed"></i>
                </div>
              ) : modalData.type === "confirm-cancel" ? (
                <div className="cancel-modal-icon-ring warning">
                  <svg
                    className="cancel-modal-icon"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 9V13"
                      stroke="#D16BFF"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />

                    <circle cx="12" cy="17" r="1" fill="#D16BFF" />

                    <path
                      d="M10.29 3.86002L1.82004 18C1.64539 18.3024 1.55296 18.6453 1.552 18.9945C1.55104 19.3438 1.64158 19.6872 1.81456 19.9905C1.98754 20.2939 2.23691 20.5464 2.53805 20.7232C2.8392 20.9 3.18159 20.9949 3.53104 20.998H20.469C20.8184 20.9949 21.1608 20.9 21.462 20.7232C21.7631 20.5464 22.0125 20.2939 22.1855 19.9905C22.3585 19.6872 22.449 19.3438 22.4481 18.9945C22.4471 18.6453 22.3547 18.3024 22.18 18L13.71 3.86002C13.5325 3.56611 13.2822 3.32288 12.9833 3.15387C12.6844 2.98486 12.347 2.89575 12.0035 2.89526C11.66 2.89476 11.3224 2.98289 11.023 3.15103C10.7236 3.31918 10.4726 3.56169 10.294 3.85502L10.29 3.86002Z"
                      stroke="#D16BFF"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ) : (
                <div className="cancel-modal-icon-ring expired">
                  <i className="bi bi-clock-history cancel-modal-status-icon expired"></i>
                </div>
              )}
            </div>

            {/* Title */}
            <h2 className="cancel-modal-title">
              {modalData.type === "success"
                ? "Payment Successful"
                : modalData.type === "failed"
                  ? "Payment Failed"
                  : modalData.type === "confirm-cancel"
                    ? "Confirm Cancellation"
                    : cancelledByUser
                      ? "Payment Cancelled"
                      : "Session Expired"}
            </h2>

            {/* Message */}
            <p className="cancel-modal-description">{modalData.message}</p>

            {/* Buttons */}
            {modalData.type === "confirm-cancel" ? (
              <div className="cancel-modal-actions">
                <button
                  className="cancel-modal-btn secondary"
                  onClick={() => {
                    setModalData({
                      open: false,
                      type: "",
                      message: "",
                    });

                    setIsRazorpayModalTimerLoader(true);
                  }}
                  disabled={cancelLoading}
                >
                  Keep Payment
                </button>

                <button
                  className="cancel-modal-btn primary"
                  disabled={cancelLoading}
                  onClick={async () => {
                    setCancelledByUser(true);
                    setCancelLoading(true);

                    clearInterval(statusIntervalRef.current);
                    clearInterval(timerIntervalRef.current);

                    localStorage.removeItem("paymentStartTime");

                    setTimeLeft(TOTAL_TIME);

                    setIsRazorpayModalTimerLoader(false);

                    await handlePaymentExpired(
                      null,
                      null,
                      "cancelled by user",
                      true,
                    );
                  }}
                >
                  {cancelLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Cancelling...
                    </>
                  ) : (
                    "Yes, Cancel"
                  )}
                </button>
              </div>
            ) : modalData.type === "success" ? (
              <button
                className="cancel-modal-btn full primary"
                onClick={handlePaymentSuccessNavigation}
              >
                Continue
              </button>
            ) : (
              <button
                className="cancel-modal-btn full primary"
                onClick={() => handleFailed(failedNavigationUrl)}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      ) : (
        <div>Redirecting to payment page...</div>
      )}
    </>
  );
}

export default RazorpayModal;
